import React from "react";
import { Box, Grid, Typography, Button, TextField, Chip } from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AutoComplete from "@material-ui/lab/Autocomplete";
import CheckIcon from "@material-ui/icons/CheckCircle";
import { useStyles } from "../../index";

/**
 * CampusSelectionStage Component
 * 
 * Handles the campus selection step of the registration form.
 * Includes school/college selection, zone selection (if enabled), and campus selection.
 * 
 * Separation of Concerns:
 * - Manages its own Formik form and validation
 * - Only passes essential data (branch, zone, schoolOrCollege) to parent on submit
 * - No business logic or API calls
 */
const CampusSelectionStage = ({
  initialValues,
  haveSchoolOrCollegeFilter,
  haveZonesFilter,
  schoolOrCollege,
  myZonesList,
  myBranchesList,
  branchesList,
  branchesData,
  paramBranch,
  onNext,
  onFieldChange,
}) => {
  const classes = useStyles();

  // Validation schema for this stage only
  const validationSchema = Yup.object().shape({
    branch: Yup.string().required("Branch is required"),
    zone: Yup.mixed().nullable(),
    schoolOrCollege: Yup.string().nullable(),
  });

  return (
    <Formik
      initialValues={{
        branch: initialValues.branch || null,
        zone: initialValues.zone || null,
        schoolOrCollege: initialValues.schoolOrCollege || schoolOrCollege,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) => {
        // Only pass essential data to parent
        onNext({
          branch: values.branch,
          zone: values.zone,
          schoolOrCollege: values.schoolOrCollege,
        });
      }}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <Box maxWidth={320} margin="auto">
              <Grid container spacing={1}>
                {haveSchoolOrCollegeFilter && (
                  <Grid item xs={12}>
                    <Box marginTop={1}>
                      <Typography variant="body2" className={classes.label}>
                        Admission for<b style={{ color: "red" }}>*</b>
                      </Typography>
                    </Box>
                    <Box className={classes.chipContainer}>
                      <Chip
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            School
                            {values.schoolOrCollege === "school" && (
                              <>
                                &nbsp;
                                <CheckIcon className="check-icon" fontSize="small" />
                              </>
                            )}
                          </Box>
                        }
                        onClick={() => {
                          setFieldValue("zone", null);
                          setFieldValue("branch", null);
                          setFieldValue("schoolOrCollege", "school");
                          if(onFieldChange) {
                            onFieldChange("schoolOrCollege", "school");
                          }
                        }}
                        className={`${classes.chip} ${
                          values.schoolOrCollege === "school" ? "selected" : ""
                        }`}
                        variant="outlined"
                      />
                      <Chip
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            College
                            {values.schoolOrCollege === "college" && (
                              <>
                                &nbsp;
                                <CheckIcon className="check-icon" fontSize="small" />
                              </>
                            )}
                          </Box>
                        }
                        onClick={() => {
                          setFieldValue("zone", null);
                          setFieldValue("branch", null);
                          setFieldValue("schoolOrCollege", "college");
                          if(onFieldChange) {
                            onFieldChange("schoolOrCollege", "college");
                          }
                        }}
                        className={`${classes.chip} ${
                          values.schoolOrCollege === "college" ? "selected" : ""
                        }`}
                        variant="outlined"
                      />
                    </Box>
                    {touched.schoolOrCollege && errors.schoolOrCollege && (
                      <Typography color="error" variant="caption">
                        {errors.schoolOrCollege}
                      </Typography>
                    )}
                  </Grid>
                )}
                {haveZonesFilter &&
                  myZonesList[values.schoolOrCollege || schoolOrCollege] &&
                  myZonesList[values.schoolOrCollege || schoolOrCollege].length > 1 && (
                    <Grid item xs={12}>
                      <Box marginTop={1}>
                        <Typography variant="body2" className={classes.label}>
                          Select Zone<b style={{ color: "red" }}>*</b>
                        </Typography>
                      </Box>
                      <AutoComplete
                        id="zone"
                        name="zone"
                        options={myZonesList[values.schoolOrCollege || schoolOrCollege] || []}
                        disabled={
                          myZonesList[values.schoolOrCollege || schoolOrCollege] &&
                          myZonesList[values.schoolOrCollege || schoolOrCollege].length === 1
                        }
                        value={
                          values.zone
                            ? {
                                zoneName: values.zone.zoneName,
                                zoneId: values.zone.zoneId,
                              }
                            : null
                        }
                        getOptionLabel={(option) => option.zoneName}
                        onChange={(_, value) => {
                          setFieldValue("zone", value);
                          setFieldValue("branch", null);
                          if(onFieldChange) {
                            onFieldChange("zone", value);
                          }
                        }}
                        renderInput={(params) => {
                          const zoneOptions = myZonesList[values.schoolOrCollege || schoolOrCollege] || [];
                          const isSingleZone = zoneOptions.length === 1;
                          return (
                            <TextField
                              {...params}
                              variant="outlined"
                              color="secondary"
                              fullWidth
                              error={touched.zone && Boolean(errors.zone)}
                              InputProps={{
                                ...params.InputProps,
                                disabled: isSingleZone,
                              }}
                            />
                          );
                        }}
                        renderOption={(option) => (
                          <Typography variant="button" className={classes.option}>
                            {option.zoneName}
                          </Typography>
                        )}
                      />
                    </Grid>
                  )}
                <Grid item xs={12}>
                  <Box marginTop={1}>
                    <Typography variant="body2" className={classes.label}>
                      Select Campus<b style={{ color: "red" }}>*</b>
                    </Typography>
                  </Box>
                  <AutoComplete
                    id="branch"
                    name="branch"
                    options={myBranchesList || []}
                    disabled={myBranchesList && myBranchesList.length === 1}
                    value={
                      values.branch
                        ? {
                            branchName: branchesData[values.branch].branchName,
                            branchCode: values.branch,
                          }
                        : null
                    }
                    getOptionLabel={(option) => option.branchName}
                    onChange={(_, value) => {
                      setFieldValue("branch", value ? value.branchCode : null);
                      if(onFieldChange) {
                        onFieldChange("branch", value ? value.branchCode : null);
                      }
                    }}
                    renderInput={(params) => {
                      if (
                        (branchesList && branchesList.length === 1) ||
                        paramBranch
                      ) {
                        return (
                          <TextField
                            {...params}
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            error={touched.branch && Boolean(errors.branch)}
                            helperText={touched.branch && errors.branch}
                            InputProps={{ disabled: true }}
                          />
                        );
                      }
                      return (
                        <TextField
                          {...params}
                          variant="outlined"
                          color="secondary"
                          fullWidth
                          error={touched.branch && Boolean(errors.branch)}
                          helperText={touched.branch && errors.branch}
                        />
                      );
                    }}
                    renderOption={(option) => (
                      <Typography variant="button" className={classes.option}>
                        {option.branchName}
                      </Typography>
                    )}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.blueGradientBtn}
                  disabled={!values.branch || (haveZonesFilter && !values.zone)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CampusSelectionStage;

